import type { FaireBrandEntity } from "@faire/playground-entity-db/entity_types/brand";
import type { FaireProductEntity } from "@faire/playground-entity-db/entity_types/product";
import type {
  EntityByType,
  EntityType,
} from "@faire/playground-entity-db/entity_types/shared";
import {
  importEntityShardModule,
  listEntityShardKeys,
  requireEntityShardModule,
} from "@faire/playground-entity-db/loadEntityShard";

import {
  withNormalizedBrandMediaUrls,
  withNormalizedProductMediaUrls,
} from "../utils/normalizePlaygroundMediaUrl";

const TOKEN_PATTERN = /^[a-z0-9_\\-]+$/i;
const IS_DEV =
  typeof process !== "undefined" &&
  typeof process.env !== "undefined" &&
  process.env.NODE_ENV !== "production";

const extractEntityFromModule = <T extends { type: string }>(
  module: unknown,
  entityType: T["type"]
): T | undefined => {
  if (typeof module !== "object" || module === null) {
    return undefined;
  }

  const candidates = Object.values(module as Record<string, unknown>);
  return candidates.find(
    (candidate): candidate is T =>
      typeof candidate === "object" &&
      candidate !== null &&
      (candidate as T).type === entityType
  );
};

const applyPlaygroundMediaNormalization = <T extends EntityType>(
  entity: EntityByType[T],
  entityType: T
): EntityByType[T] => {
  if (entityType === "product") {
    return withNormalizedProductMediaUrls(
      entity as FaireProductEntity
    ) as EntityByType[T];
  }
  if (entityType === "brand") {
    return withNormalizedBrandMediaUrls(
      entity as FaireBrandEntity
    ) as EntityByType[T];
  }
  return entity;
};

const entityCache: { [T in EntityType]: Map<string, EntityByType[T]> } = {
  product: new Map(),
  brand: new Map(),
  retailer: new Map(),
  order: new Map(),
  theme: new Map(),
  collection: new Map(),
  category: new Map(),
  review: new Map(),
  catalog: new Map(),
  r2bStagingBrand: new Map(),
  r2bAvailableBrand: new Map(),
  r2bReferralBrand: new Map(),
  advertiserProfile: new Map(),
  adCampaign: new Map(),
  adPerformancePeriod: new Map(),
};

const loadEntityByToken = async <T extends EntityType>(
  token: string,
  entityType: T
): Promise<EntityByType[T] | undefined> => {
  if (!TOKEN_PATTERN.test(token)) {
    return undefined;
  }

  // A dynamic import for a missing token is treated as a compilation error by
  // the development server, even though missing snapshots are an expected
  // state while the entity database is being updated. Use the static context
  // in development so the lookup can fail normally and return undefined.
  if (IS_DEV) {
    return loadEntityByTokenSync(token, entityType);
  }

  const cache = entityCache[entityType];
  const cached = cache.get(token);
  if (cached) {
    return cached;
  }

  try {
    const module = await importEntityShardModule(entityType, token);
    const candidate = extractEntityFromModule<EntityByType[T]>(
      module,
      entityType
    );
    if (candidate) {
      const normalized = applyPlaygroundMediaNormalization(
        candidate,
        entityType
      );
      cache.set(token, normalized);
      return normalized;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

const loadEntityByTokenSync = <T extends EntityType>(
  token: string,
  entityType: T
): EntityByType[T] | undefined => {
  try {
    const module = requireEntityShardModule(entityType, token);
    const candidate = extractEntityFromModule<EntityByType[T]>(
      module,
      entityType
    );
    if (candidate) {
      const normalized = applyPlaygroundMediaNormalization(
        candidate,
        entityType
      );
      if (!IS_DEV) {
        entityCache[entityType].set(token, normalized);
      }
      return normalized;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

const getEntityTokensByType = <T extends EntityType>(
  entityType: T
): string[] => {
  // require.context is resolved at compile time; newly added shard files may
  // require a dev-server restart before they appear in sync token enumeration.
  return listEntityShardKeys()
    .filter((key) => key.startsWith(`./${entityType}/`))
    .map((key) => key.replace(`./${entityType}/`, "").replace(/\.ts$/, ""))
    .filter((token) => token !== "default")
    .sort((left, right) => left.localeCompare(right));
};

export const getEntityByToken = <T extends EntityType>(
  token: string,
  entityType: T
): EntityByType[T] | undefined => {
  if (!TOKEN_PATTERN.test(token)) {
    return undefined;
  }
  if (!IS_DEV) {
    const cached = entityCache[entityType].get(token);
    if (cached) {
      return cached;
    }
  }
  return loadEntityByTokenSync(token, entityType);
};

export const getEntityByTokenAsync = async <T extends EntityType>(
  token: string,
  entityType: T
): Promise<EntityByType[T] | undefined> => {
  if (!IS_DEV) {
    const cached = getEntityByToken(token, entityType);
    if (cached) {
      return cached;
    }
  }
  return loadEntityByToken(token, entityType);
};

export const getDefaultEntity = <T extends EntityType>(
  entityType: T
): EntityByType[T] => {
  const entity = getEntityByToken("default", entityType);
  if (!entity) {
    throw new Error(
      `Missing required default entity shard at @faire/playground-entity-db/entity_db/${entityType}/default.ts`
    );
  }
  return entity;
};

export const getAllEntities = <T extends EntityType>(
  entityType: T
): Array<EntityByType[T]> => {
  return getEntityTokensByType(entityType)
    .map((token) => getEntityByToken(token, entityType))
    .filter((entity): entity is EntityByType[T] => entity !== undefined);
};

export const getRandomEntitiesSync = <T extends EntityType>(
  entityType: T,
  count: number
): Array<EntityByType[T]> => {
  if (count <= 0) {
    return [];
  }
  const tokenPool = getEntityTokensByType(entityType);

  const shuffled = [...tokenPool].sort(() => Math.random() - 0.5);
  const selected = shuffled.slice(0, count);

  return selected
    .map((token) => getEntityByToken(token, entityType))
    .filter((entity): entity is EntityByType[T] => entity !== undefined);
};

export const getProductsByBrandToken = (
  brandToken: string
): FaireProductEntity[] => {
  if (!brandToken) {
    return [];
  }
  return getAllEntities("product").filter(
    (product) => product.brandToken === brandToken
  );
};
